import { cn } from '@/lib/utils';

/**
 * Small rounded chips describing a main event's format and length. Rendered
 * next to the status pill on event cards and in the event header, so the
 * styling mirrors the existing STATUS_COLORS chips.
 */

const CHIP_BASE =
  'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border shrink-0';

/** Format → label + chip color. Extensible for future formats (Draft…). */
const FORMAT_BADGES: Record<string, { label: string; className: string }> = {
  SURVIVOR: {
    label: 'Survivor',
    className: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
  },
  STANDARD: {
    label: "Pick'em",
    className: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
  },
  BEST_BALL: {
    label: 'Best Ball',
    className: 'bg-rose-500/10 border-rose-500/30 text-rose-400',
  },
  DRAFT_PPR: {
    label: 'PPR',
    className: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
  },
};

export function FormatBadge({ format, className }: { format: string; className?: string }) {
  const badge = FORMAT_BADGES[format];
  if (!badge) return null;
  return <span className={cn(CHIP_BASE, badge.className, className)}>{badge.label}</span>;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Duration bucket for an event. An explicit admin-set durationClass wins;
 * the date-derived guess (≤10 days Weekly, ≤40 Monthly, else Season) is the
 * fallback for legacy events created before the Event Length selector.
 */
export function eventLengthLabel(
  startTime: string | Date,
  endTime: string | Date,
  durationClass?: 'WEEKLY' | 'MONTHLY' | 'SEASON' | null
): string {
  if (durationClass === 'WEEKLY') return 'Weekly';
  if (durationClass === 'MONTHLY') return 'Monthly';
  if (durationClass === 'SEASON') return 'Season';
  const days = (new Date(endTime).getTime() - new Date(startTime).getTime()) / DAY_MS;
  if (days <= 10) return 'Weekly';
  if (days <= 40) return 'Monthly';
  return 'Season';
}

export function LengthBadge({
  startTime,
  endTime,
  durationClass,
  className,
}: {
  startTime: string | Date;
  endTime: string | Date;
  durationClass?: 'WEEKLY' | 'MONTHLY' | 'SEASON' | null;
  className?: string;
}) {
  const label = eventLengthLabel(startTime, endTime, durationClass);
  // Monthly events carry a discrete month marker so users know WHICH month
  // (e.g. "Monthly · Sep"). Weekly/Season stay bare — the rail/dates cover them.
  const suffix =
    label === 'Monthly'
      ? ` · ${new Date(startTime).toLocaleString('en-US', { month: 'short' })}`
      : '';
  // translucent neutral so the chip reads on both dark (header) and parchment (card) surfaces
  return (
    <span className={cn(CHIP_BASE, 'bg-warm-gray/10 border-warm-gray/30 text-warm-gray', className)}>
      {label}
      {suffix && <span className="normal-case font-semibold">{suffix}</span>}
    </span>
  );
}
